import bannerImage from '@assets/landing/section1/banner_img.jpg';
import dashboardImage from '@assets/landing/section1/dashboard_img.png';
import logo from '@assets/logo.svg';
import { Box } from '@components/ui/atoms/Box';
import { Button } from '@components/ui/atoms/Button';
import { Container } from '@components/ui/atoms/Container';
import { Flex } from '@components/ui/atoms/Flex';
import { Heading } from '@components/ui/atoms/Heading';
import { Image } from '@components/ui/atoms/Image';
import { Text } from '@components/ui/atoms/Text';
import {
  fadeInUp,
  floatHover8Reverse,
  floatHover15,
  slideInRightLarge,
  slideUpFade,
} from '@shared/utils/animationsUtil';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { loginPath } from '../auth/urls';
import { LandingPageHeader } from './LandingPageHeader';

const NotificationToast: React.FC<{
  message: string;
  subMessage?: string;
  icon?: React.ReactNode;
  isLarge?: boolean;
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
  opacity?: number;
  bg?: string;
  position?: string;
  animation?: string;
}> = ({
  message,
  subMessage,
  isLarge,
  top,
  right,
  bottom,
  left,
  opacity,
  bg,
  position,
  animation,
}) => (
  <Box
    position={position || 'absolute'}
    top={top}
    right={right}
    bottom={bottom}
    left={left}
    opacity={opacity}
    bg={bg || 'rgba(255, 255, 255, 0.05)'}
    backdropFilter="blur(20px)"
    borderRadius="xl"
    p={isLarge ? '1.25rem 2rem' : '0.75rem 1.5rem'}
    border="1px solid rgba(255, 255, 255, 0.1)"
    boxShadow="0 4px 30px rgba(0, 0, 0, 0.1)"
    zIndex="10"
    animation={animation}
  >
    <Flex alignItems="center" gap={{ base: '0.75rem', md: '1rem' }}>
      <Flex
        bg="blue.900"
        borderRadius="full"
        w={{ base: '36px', md: '44px' }}
        h={{ base: '36px', md: '44px' }}
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        <Image src={logo} w={{ base: 5, md: 6 }} h={{ base: 5, md: 6 }} alt="logo" />
      </Flex>
      <Box>
        <Text color="white" fontWeight="500" fontSize={isLarge ? 'sm' : 'xs'}>
          {message}
        </Text>
        {subMessage && (
          <Text color="whiteAlpha.600" fontSize="xs" mt="0.25rem">
            {subMessage}
          </Text>
        )}
      </Box>
    </Flex>
  </Box>
);

export const BannerSection: React.FC = () => {
  return (
    <Box position="relative" w="full" bg="#0B0B0F" overflow="hidden">
      <LandingPageHeader />

      {/* Hero Section */}
      <Box position="relative" pt="8rem">
        {/* Background Image with Gradient Overlay */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex="0"
          pointerEvents="none"
        >
          <Image
            src={bannerImage}
            alt="Hero background"
            w="full"
            h="full"
            objectFit="cover"
            opacity="0.8"
          />
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="linear-gradient(100deg, rgb(0 0 0 / 21%) 0%, rgb(137 137 161 / 15%) 100%)"
          />
        </Box>

        <Container maxW="container.xl" position="relative" zIndex="1">
          <Flex direction={{ base: 'column', lg: 'row' }} align="center" gap="4rem">
            <Box flex="1.2" animation={`${fadeInUp} 1s ease-out forwards`}>
              <Heading
                color="white"
                fontSize={{ base: '3xl', md: '5xl', lg: '6xl' }}
                fontWeight="600"
                lineHeight="1.1"
                mb="1.5rem"
                letterSpacing="-0.02em"
              >
                Empowering
                <br />
                Seamless Transactions
              </Heading>
              <Text
                color="#cbc0f5"
                fontSize={{ base: 'xl', md: '2xl' }}
                fontWeight="600"
                mb="2rem"
                opacity="0.9"
              >
                Simple, Secure, Swift – Enhancing
                <br />
                Your Payment Journey in Bhutan
              </Text>
              <Text color="whiteAlpha.700" fontSize="sm" maxW="500px" mb="2.5rem" lineHeight="1.6">
                Accept international payments, enable faster payouts, automate financial processes,
                and grow more revenue for your business.
              </Text>

              <Flex align="center" gap="2rem">
                <RouterLink to={loginPath}>
                  <Button visual="primary" size="lg" px="2rem" borderRadius="md">
                    Get Started For Free
                  </Button>
                </RouterLink>
                <Text color="whiteAlpha.800" fontWeight="500">
                  Trusted by 600+ users
                </Text>
              </Flex>
            </Box>

            <Box flex="1" position="relative" h="500px" display={{ base: 'none', lg: 'block' }}>
              {/* Floating elements from the design */}
              <NotificationToast
                message="Your invoice is created"
                subMessage="Please check the invoice preview here"
                top="34%"
                right="18%"
                opacity={0.4}
                animation={`${slideInRightLarge} 0.8s ease-out forwards 0.2s, ${floatHover8Reverse} 6s ease-in-out infinite`}
              />

              <NotificationToast
                message="Your invoice has been sent successfully!"
                isLarge={true}
                top="60%"
                right="-10%"
                opacity={0.8}
                bg="rgb(255 255 255 / 20%)"
                animation={`${slideInRightLarge} 0.8s ease-out forwards 0.4s, ${floatHover15} 6s ease-in-out infinite`}
              />

              <NotificationToast
                message="The customer viewed your invoice"
                bottom="0%"
                right="10%"
                opacity={0.4}
                animation={`${slideInRightLarge} 0.8s ease-out forwards 0.6s, ${floatHover8Reverse} 6s ease-in-out infinite`}
              />
            </Box>
          </Flex>

          {/* Dashboard Image at the Bottom overlaying slightly */}

          <Box
            mt="2rem"
            overflow="hidden"
            display="flex"
            flexDirection="column"
            alignItems="center"
            flex={1}
            animation={`${slideUpFade} 1.2s ease-out forwards`}
          >
            <Box
              display={{ base: 'flex', lg: 'none' }}
              position="relative"
              maxW="380px"
              h="70px"
              justifyContent="center"
              m="2rem"
            >
              <NotificationToast
                position="relative"
                message="Your invoice has been sent successfully!"
                isLarge={false}
                top="0%"
                right="0%"
                opacity={0.8}
                bg="rgb(255 255 255 / 20%)"
              />
            </Box>

            <Image src={dashboardImage} alt="Bhutan Payments Dashboard" w="full" opacity="1" />
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

BannerSection.displayName = 'BannerSection';
